import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { ModaleSignatureDocument } from '../components/signature/ModaleSignatureDocument';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import {
  urlDocumentPedagogique,
  useAvancerSuiviPedagogique,
  useDelivrerAttestationEntree,
  useDemanderSignatureDocument,
  useDeposerModelePedagogique,
  useDocumentsPedagogiques,
  type EtatDocumentPedagogique,
  type ReponseDocumentsPedagogiques,
} from '../lib/usePedagogique';
import { LigneModeleDepose } from '../components/documents/LigneModeleDepose';
import type { RoleSignataire } from '../lib/useSignatures';
import { ErreurApi } from '../lib/api';
import { useToast } from '../components/ui/useToast';
import { SuiviDocumentaire } from '../components/documents/SuiviDocumentaire';

const CLASSE_CHAMP = 'w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm';

function horodatage(valeur: string | null): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/**
 * État lisible d'une ligne. Trois colonnes de pastilles vides ne disaient pas si
 * un document était en retard, en cours ou sans objet : une phrase le dit.
 */
function etatLisible(document: EtatDocumentPedagogique): {
  texte: string;
  ton: 'info' | 'success' | 'warning' | 'mint' | 'neutre';
} {
  if (!document.signable) {
    return document.transmis
      ? { texte: `Transmise le ${horodatage(document.horodatage)}`, ton: 'mint' }
      : document.documentId
        ? { texte: `Générée le ${horodatage(document.horodatage)}`, ton: 'info' }
        : { texte: 'Non générée', ton: 'neutre' };
  }

  if (!document.demandeSignatureId) return { texte: 'Non demandé', ton: 'neutre' };
  if (document.statutSignature === 'ANNULEE') return { texte: 'Demande annulée', ton: 'warning' };
  if (document.statutSignature === 'SIGNEE') {
    return { texte: `Signé le ${horodatage(document.horodatage)}`, ton: 'success' };
  }
  return {
    texte: `En attente · ${document.signesCount}/${document.total} signé${
      document.signesCount > 1 ? 's' : ''
    }`,
    ton: 'warning',
  };
}

const CLASSE_LIEN_PDF =
  'inline-flex min-h-[36px] items-center gap-1 text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]';

/**
 * Écran « Documents pédagogiques » et suivi documentaire (CDC §6.3 et §6.4).
 *
 * Les trois statuts — vérifié, validé, transmis — sont lus sur les demandes de
 * signature et les documents générés du dossier consulté. Un document jamais
 * demandé n'est pas « en retard » : il est absent, et l'écran l'affiche comme tel.
 *
 * Rien ne se signe sur son seul titre : la demande verse au dossier le fichier
 * que le signataire lit — copie du modèle déposé, fiche REAC déposée pour la
 * formation ou, à défaut, composée — et
 * l'écran ne propose « Faire signer » que lorsque l'API n'y oppose rien.
 */
export function PedDocumentsPage(): JSX.Element {
  const { site } = usePerimetre();
  const [apprenantId, setApprenantId] = useState<string | undefined>(undefined);
  const [aDemander, setADemander] = useState<EtatDocumentPedagogique | null>(null);
  const [aSigner, setASigner] = useState<EtatDocumentPedagogique | null>(null);

  const { data, isLoading, error } = useDocumentsPedagogiques(site, apprenantId);
  // Suivi Vérifié / Validé / Transmis : le dernier document touché porte
  // l'éventuel refus de l'API, les autres lignes restent muettes.
  const avancerSuivi = useAvancerSuiviPedagogique();
  const [documentSuivi, setDocumentSuivi] = useState<string | null>(null);

  const compteurs = data?.compteurs;
  const dossier = data?.apprenant ?? null;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Documents pédagogiques"
        sousTitre="Pôle pédagogique · documents signables sur tablette, statuts vérifié / validé / transmis"
        actions={
          <Link
            to="/emargement"
            className="rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
          >
            Émargement
          </Link>
        }
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Compteur
          libelle="Vérifiés"
          valeur={compteurs ? `${compteurs.verifies}/${compteurs.total}` : '—'}
          detail={data?.legende['VERIFIE'] ?? 'contrôle de conformité'}
          ton="blue"
        />
        <Compteur
          libelle="Validés"
          valeur={compteurs ? `${compteurs.valides}/${compteurs.total}` : '—'}
          detail={data?.legende['VALIDE'] ?? 'document approuvé'}
          ton="mint"
        />
        <Compteur
          libelle="Transmis"
          valeur={compteurs ? `${compteurs.transmis}/${compteurs.total}` : '—'}
          detail={data?.legende['TRANSMIS'] ?? 'envoyé au destinataire'}
          ton="navy"
        />
      </div>

      {error instanceof ErreurApi && <MessageErreur message={error.message} />}

      <Carte
        titre={dossier ? `Suivi documentaire · ${dossier.nom}` : 'Suivi documentaire'}
        actions={
          data && data.apprenants.length > 0 ? (
            <select
              value={dossier?.id ?? ''}
              onChange={(evenement) => setApprenantId(evenement.target.value)}
              className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
              aria-label="Apprenant consulté"
            >
              {data.apprenants.map((apprenant) => (
                <option key={apprenant.id} value={apprenant.id}>
                  {apprenant.nom} · {apprenant.siteCode}
                </option>
              ))}
            </select>
          ) : undefined
        }
      >
        {isLoading && <Chargement />}

        {!isLoading && data && data.apprenants.length === 0 && (
          <div className="px-4 sm:px-6 py-8 text-sm text-codialis-gray">
            Aucun apprenant n'est rattaché à ce périmètre.
          </div>
        )}

        {dossier && (
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Document</th>
                  <th className="px-4 py-3 text-left font-bold">Portée</th>
                  <th className="px-4 py-3 text-left font-bold">État</th>
                  <th className="px-4 py-3 text-left font-bold">Signataires</th>
                  <th className="px-4 py-3 text-right font-bold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.documents.map((document) => {
                  const etat = etatLisible(document);
                  const restant = document.signataires.find((signataire) => !signataire.signeLe);
                  return (
                    <tr key={document.code} className="transition hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-codialis-navy">
                        {document.libelle}
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">{document.portee}</td>
                      <td className="px-4 py-3">
                        {etat.ton === 'neutre' ? (
                          <span className="text-xs text-codialis-gray">{etat.texte}</span>
                        ) : (
                          <Pastille texte={etat.texte} ton={etat.ton} />
                        )}
                        {document.suivi && document.documentId && (
                          <SuiviDocumentaire
                            suivi={document.suivi}
                            peutAvancer
                            enCours={avancerSuivi.isPending}
                            erreur={
                              documentSuivi === document.documentId &&
                              avancerSuivi.error instanceof ErreurApi
                                ? avancerSuivi.error.message
                                : null
                            }
                            onAvancer={(demande) => {
                              const documentId = document.documentId;
                              if (!documentId) return;
                              setDocumentSuivi(documentId);
                              avancerSuivi.mutate({ documentId, ...demande });
                            }}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">
                        {document.signataires.length === 0
                          ? '—'
                          : document.signataires
                              .map(
                                (signataire) =>
                                  `${signataire.nom}${signataire.signeLe ? ' (signé)' : ''}`,
                              )
                              .join(' · ')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {document.documentId && (
                            <a
                              href={urlDocumentPedagogique(document.documentId)}
                              target="_blank"
                              rel="noreferrer"
                              className={CLASSE_LIEN_PDF}
                            >
                              <FileText className="h-3.5 w-3.5" aria-hidden />
                              Lire
                            </a>
                          )}
                          {!document.signable ? null : document.refusDemande === null ? (
                            <button
                              type="button"
                              onClick={() => setADemander(document)}
                              className="rounded-xl bg-codialis-navy px-3 py-2 text-xs font-semibold text-white transition hover:bg-codialis-deep"
                            >
                              Faire signer
                            </button>
                          ) : restant && document.statutSignature !== 'ANNULEE' ? (
                            <button
                              type="button"
                              onClick={() => setASigner(document)}
                              className="rounded-xl bg-codialis-mint px-3 py-2 text-xs font-semibold text-codialis-navy transition hover:brightness-95"
                            >
                              Signer maintenant
                            </button>
                          ) : document.statutSignature === 'SIGNEE' ? (
                            <span className="text-xs font-semibold text-codialis-gray">
                              Signé
                            </span>
                          ) : (
                            <span className="max-w-[220px] text-left text-[11px] text-codialis-gray">
                              {document.refusDemande}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Carte titre="Signification des statuts" sansBordure>
          <div className="space-y-3 p-4 sm:p-6">
            <div className="flex items-start gap-3">
              <Pastille texte="Vérifié" ton="info" />
              <div className="text-xs text-codialis-gray">{data?.legende['VERIFIE']}</div>
            </div>
            <div className="flex items-start gap-3">
              <Pastille texte="Validé" ton="success" />
              <div className="text-xs text-codialis-gray">{data?.legende['VALIDE']}</div>
            </div>
            <div className="flex items-start gap-3">
              <Pastille texte="Transmis" ton="mint" />
              <div className="text-xs text-codialis-gray">{data?.legende['TRANSMIS']}</div>
            </div>
          </div>
        </Carte>

        <Carte titre="Référentiel de la formation" sansBordure>
          <div className="space-y-2 p-4 sm:p-6 text-xs text-codialis-gray">
            {!dossier?.formation && <p>Aucune formation n'est rattachée à ce dossier.</p>}

            {dossier?.formation && (
              <>
                <div className="text-sm font-semibold text-codialis-navy">
                  {dossier.formation}
                  {dossier.rncp ? ` · RNCP ${dossier.rncp}` : ''}
                  {dossier.codeDiplome ? ` · code diplôme ${dossier.codeDiplome}` : ''}
                </div>
                {dossier.niveau && <div>{dossier.niveau}</div>}
                {dossier.modules.length === 0 && (
                  <p>Le programme de cette formation n'a pas encore de blocs saisis.</p>
                )}
                {dossier.modules.map((module) => (
                  <div key={module.libelle} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-codialis-teal" />
                    <span>
                      {module.libelle} ({module.heures} h)
                    </span>
                  </div>
                ))}
                {(dossier.heuresEcrits > 0 || dossier.heuresEcf > 0) && (
                  <div className="pt-1">
                    Écrits professionnels : {dossier.heuresEcrits} h · évaluations en cours de
                    formation : {dossier.heuresEcf} h
                  </div>
                )}
              </>
            )}
          </div>
        </Carte>

        <CarteAttestation
          dossier={dossier}
          attestation={
            data?.documents.find((document) => document.code === 'ATTESTATION_ENTREE') ?? null
          }
        />
      </div>

      {data && <CarteModeles donnees={data} />}

      {aDemander && dossier && (
        <ModaleDemande
          document={aDemander}
          dossier={dossier}
          roles={data?.roles ?? []}
          onFermer={() => setADemander(null)}
        />
      )}

      {aSigner?.demandeSignatureId && (
        <ModaleSignatureDocument
          libelle={aSigner.libelle}
          urlLecture={aSigner.documentId ? urlDocumentPedagogique(aSigner.documentId) : null}
          demandeSignatureId={aSigner.demandeSignatureId}
          signataires={aSigner.signataires}
          empreinteDocument={aSigner.empreinteDocument}
          onFermer={() => setASigner(null)}
        />
      )}
    </div>
  );
}

type DossierAffiche = NonNullable<ReponseDocumentsPedagogiques['apprenant']>;

interface LigneSignataire {
  role: RoleSignataire;
  nom: string;
  email: string;
}

/**
 * Attestation d'entrée en formation (CDC §6.1), délivrée ici par le pôle
 * pédagogique — assistant(e) de site compris(e) — sans passer par le dossier
 * administratif. L'API la refuse avant l'entrée : elle atteste une entrée
 * constatée.
 */
function CarteAttestation({
  dossier,
  attestation,
}: {
  dossier: DossierAffiche | null;
  attestation: EtatDocumentPedagogique | null;
}): JSX.Element {
  const notifier = useToast();
  const delivrer = useDelivrerAttestationEntree();

  return (
    <Carte titre="Attestation d'entrée en formation" sansBordure>
      <div className="space-y-3 p-4 sm:p-6">
        <p className="text-xs text-codialis-gray">
          Elle reprend l'identité, la formation, l'antenne et la date d'entrée du dossier, sans
          ressaisie, et se range dans le dossier de l'apprenant.
        </p>
        <div className={CLASSE_CHAMP}>{dossier ? dossier.nom : 'Aucun apprenant'}</div>
        {attestation?.documentId && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-codialis-gray">
            <span>Générée le {horodatage(attestation.horodatage)}</span>
            <a
              href={urlDocumentPedagogique(attestation.documentId)}
              target="_blank"
              rel="noreferrer"
              className={CLASSE_LIEN_PDF}
            >
              <FileText className="h-3.5 w-3.5" aria-hidden />
              Lire l'attestation
            </a>
          </div>
        )}
        {delivrer.error instanceof ErreurApi && <MessageErreur message={delivrer.error.message} />}
        {dossier ? (
          <BoutonPrincipal
            onClick={() =>
              delivrer.mutate(dossier.id, {
                onSuccess: (resultat) =>
                  notifier([resultat.message, ...resultat.avertissements].join(' ')),
              })
            }
            disabled={delivrer.isPending}
          >
            {delivrer.isPending
              ? 'Génération…'
              : attestation?.documentId
                ? "Régénérer l'attestation"
                : "Délivrer l'attestation"}
          </BoutonPrincipal>
        ) : null}
      </div>
    </Carte>
  );
}

/**
 * Modèles des documents à signer, tenus par le pôle pédagogique — assistant(e)
 * de site compris(e) — et la direction (TF-477) : un modèle commun aux deux
 * antennes par document, une fiche REAC par formation. Le dépôt le plus récent
 * sert aux prochaines demandes ; ce qui a déjà été remis garde sa copie, donc
 * ce qui a été signé reste ce qui a été lu. Sans droit de dépôt, la carte se
 * lit seulement.
 */
function CarteModeles({ donnees }: { donnees: ReponseDocumentsPedagogiques }): JSX.Element {
  const deposer = useDeposerModelePedagogique();
  const peutDeposer = donnees.refusDepot === null;
  const envoyer =
    (cible: { code: string } | { formationId: string }) =>
    (fichier: File): Promise<string> =>
      deposer.mutateAsync({ cible, fichier }).then((reponse) => reponse.message);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Carte titre="Modèles des documents à signer">
        <p className="px-4 py-3 text-xs text-codialis-gray sm:px-6">
          Un modèle PDF par document, commun aux antennes EST et SUD. Sans modèle, le document ne peut pas être mis à
          la signature : le signataire doit pouvoir le lire. Un nouveau modèle sert aux prochaines demandes, sans
          réécrire ce qui a déjà été remis.
        </p>
        {!peutDeposer && donnees.refusDepot && (
          <p className="px-4 pb-3 text-[11px] text-codialis-gray sm:px-6">{donnees.refusDepot}</p>
        )}
        {donnees.modeles.map((modele) => (
          <LigneModeleDepose
            key={modele.code}
            libelle={modele.libelle}
            depots={modele}
            sansModele="Tant qu’il manque, ce document ne part pas à la signature."
            {...(peutDeposer ? { deposer: envoyer({ code: modele.code }) } : {})}
          />
        ))}
      </Carte>

      <Carte titre="Fiches REAC par formation">
        <p className="px-4 py-3 text-xs text-codialis-gray sm:px-6">
          La fiche déposée pour une formation est remise telle quelle à ses apprenants. Sans fiche déposée, la
          plateforme la compose depuis les blocs du programme.
        </p>
        {donnees.fichesReac.length === 0 && (
          <p className="px-4 pb-4 text-xs text-codialis-gray sm:px-6">Aucun programme validé.</p>
        )}
        {donnees.fichesReac.map((fiche) => (
          <LigneModeleDepose
            key={fiche.formation.id}
            libelle={fiche.formation.titre}
            precision={`${fiche.formation.code}${fiche.formation.actif ? '' : ' · formation désactivée'}`}
            depots={fiche}
            sansModele="Composée depuis les blocs du programme."
            etatAbsent={{ texte: 'Composée', ton: 'neutral' }}
            libelleDepot="Déposer la fiche"
            {...(peutDeposer ? { deposer: envoyer({ formationId: fiche.formation.id }) } : {})}
          />
        ))}
      </Carte>
    </div>
  );
}

/**
 * Création d'une demande sans quitter l'écran.
 *
 * Ni l'intitulé ni le fichier ne se saisissent : l'API verse au dossier le
 * document du référentiel — copie du modèle, ou fiche REAC composée — et la
 * demande en prend l'empreinte.
 */
function ModaleDemande({
  document,
  dossier,
  roles,
  onFermer,
}: {
  document: EtatDocumentPedagogique;
  dossier: DossierAffiche;
  roles: { valeur: RoleSignataire; libelle: string }[];
  onFermer: () => void;
}): JSX.Element {
  const creer = useDemanderSignatureDocument();
  const [signataires, setSignataires] = useState<LigneSignataire[]>(() => {
    const base: LigneSignataire[] = [{ role: 'APPRENANT', nom: dossier.nom, email: '' }];
    // Les coordonnées du représentant légal ne sont pas en base : la ligne est
    // proposée avec son nom à saisir plutôt que silencieusement oubliée.
    if (dossier.mineur) base.push({ role: 'REPRESENTANT_LEGAL', nom: '', email: '' });
    return base;
  });

  const incomplet = signataires.some((signataire) => signataire.nom.trim().length < 2);

  function modifier(index: number, champ: keyof LigneSignataire, valeur: string): void {
    setSignataires((lignes) =>
      lignes.map((ligne, rang) => (rang === index ? { ...ligne, [champ]: valeur } : ligne)),
    );
  }

  return (
    <Modale
      titre="Faire signer"
      sousTitre={document.libelle}
      ouverte
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-4 p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-codialis-gray">Document</div>
            <div className={CLASSE_CHAMP}>{document.libelle}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-codialis-gray">Apprenant</div>
            <div className={CLASSE_CHAMP}>
              {dossier.nom} · {dossier.siteCode}
            </div>
          </div>
        </div>

        {dossier.mineur && (
          <p className="rounded-xl bg-amber-50 p-3 text-xs text-codialis-navy">
            Apprenant mineur : son représentant légal doit co-signer. Ses coordonnées ne sont pas
            enregistrées dans le dossier, saisissez son nom ci-dessous.
          </p>
        )}

        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-codialis-gray">Signataires</div>
          {signataires.map((signataire, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,10rem)_1fr_1fr_auto]">
              <select
                value={signataire.role}
                onChange={(evenement) => modifier(index, 'role', evenement.target.value)}
                className={CLASSE_CHAMP}
                aria-label={`Rôle du signataire ${index + 1}`}
              >
                {roles.map((role) => (
                  <option key={role.valeur} value={role.valeur}>
                    {role.libelle}
                  </option>
                ))}
              </select>
              <input
                value={signataire.nom}
                onChange={(evenement) => modifier(index, 'nom', evenement.target.value)}
                placeholder="Nom et prénom"
                className={CLASSE_CHAMP}
                aria-label={`Nom du signataire ${index + 1}`}
              />
              <input
                value={signataire.email}
                onChange={(evenement) => modifier(index, 'email', evenement.target.value)}
                placeholder="E-mail (facultatif)"
                type="email"
                className={CLASSE_CHAMP}
                aria-label={`E-mail du signataire ${index + 1}`}
              />
              <button
                type="button"
                onClick={() =>
                  setSignataires((lignes) => lignes.filter((_, rang) => rang !== index))
                }
                disabled={signataires.length === 1}
                className="rounded-xl border border-gray-200 px-3 py-2.5 text-xs font-semibold text-codialis-gray transition hover:bg-gray-50 disabled:opacity-40"
              >
                Retirer
              </button>
            </div>
          ))}
          <BoutonSecondaire
            onClick={() =>
              setSignataires((lignes) => [...lignes, { role: 'CFA', nom: '', email: '' }])
            }
          >
            Ajouter un signataire
          </BoutonSecondaire>
        </div>

        {creer.error instanceof ErreurApi && <MessageErreur message={creer.error.message} />}

        <div className="flex flex-wrap gap-2">
          <BoutonPrincipal
            onClick={() =>
              creer.mutate(
                {
                  code: document.code,
                  apprenantId: dossier.id,
                  signataires: signataires.map((signataire) => ({
                    role: signataire.role,
                    nom: signataire.nom.trim(),
                    ...(signataire.email.trim() ? { email: signataire.email.trim() } : {}),
                  })),
                },
                { onSuccess: onFermer },
              )
            }
            disabled={incomplet || creer.isPending}
          >
            {creer.isPending ? 'Création…' : 'Créer la demande'}
          </BoutonPrincipal>
          <BoutonSecondaire onClick={onFermer} disabled={creer.isPending}>
            Annuler
          </BoutonSecondaire>
        </div>
      </div>
    </Modale>
  );
}

